import {
  useBatchStockDetailQuery,
  useAddUpdateBatchStockMutationMutation,
} from "generated/graphql"
import { Icon, Spinner } from "@blueprintjs/core"
import CustomButton from "components/Atoms/CustomButton"
import CustomCard from "components/Atoms/CustomCard"
import CustomTag from "components/Atoms/CustomTag"
import useFileUpload from "hooks/useFileUpload"
import React, { useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import AppToaster from "utils/AppToaster"
import { graphqlError } from "utils/parseError"
import dayjs from "dayjs"

/* ─── helpers ─────────────────────────────────────────────── */

const InfoRow = ({
  label,
  value,
}: {
  label: string
  value?: string | number | null
}) => (
  <div>
    <div className="text-xs text-gray-500 mb-0.5">{label}</div>
    <div className="text-sm font-medium text-gray-800">{value ?? "—"}</div>
  </div>
)

const StockRow = ({
  label,
  value,
}: {
  label: string
  value?: number | null
}) => (
  <div className="px-3">
    <div className="flex justify-between  border-b border-gray-200">
      <span className="text-xs font-semibold">{label}</span>
      <span className="text-xs font-semibold">{value ?? 0}</span>
    </div>
    <hr className="border-gray-200" />
  </div>
)

/* ─── main page ───────────────────────────────────────────── */

const BatchDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [coaUrl, setCoaUrl] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  /* Query */
  const { data, loading, error, refetch } = useBatchStockDetailQuery({
    variables: { id },
    skip: !id,
    onCompleted: d => {
      if (d?.batchStockDetail?.coaCertificate) {
        setCoaUrl(d.batchStockDetail.coaCertificate)
      }
    },
  })
  const batch = data?.batchStockDetail

  /* File upload */
  const [, handleUpload] = useFileUpload("batch-coa", {
    onCompleted: (files: Record<string, any>) => {
      const uploaded = Object.values(files).find(f => f.status === "uploaded")
      if (uploaded?.url) {
        setCoaUrl(uploaded.url)
      }
    },
  })

  /* Mutation */
  const [saveStock, { loading: saving }] =
    useAddUpdateBatchStockMutationMutation({
      onCompleted: () => {
        AppToaster.show({
          message: "Batch saved successfully!",
          intent: "success",
          icon: "tick",
        })
        refetch()
      },
      onError: err => {
        AppToaster.show({
          message: graphqlError(err.message),
          intent: "danger",
          icon: "cross",
        })
      },
    })

  function handleSave() {
    if (!batch?.suppliedBy?.id) return
    saveStock({
      variables: {
        id,
        data: {
          suppliedById: batch.suppliedBy.id,
          coaCertificate: coaUrl,
        },
      },
    })
  }

  if (loading)
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Spinner size={40} />
      </div>
    )

  if (error || !batch)
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-gray-500 gap-2">
        <Icon icon="warning-sign" size={32} intent="danger" />
        <span>Failed to load batch details.</span>
      </div>
    )

  const totalStock =
    (batch.currentStockCount ?? 0) +
    (batch.holdStockCount ?? 0) +
    (batch.alternateStockCount ?? 0) +
    (batch.intransitStockCount ?? 0) +
    (batch.dumpedStockCount ?? 0)

  const isCertExpired = batch.expiryOn && new Date(batch.expiryOn) < new Date()

  return (
    <div className="p-4 bg-white h-screen">
      {/* Back + title bar */}
      <div className="flex items-center gap-3 mb-4">
        <CustomButton minimal icon="arrow-left" onClick={() => navigate(-1)} />
        <h3 className="text-gray-800 font-semibold m-0">Batch </h3>
      </div>
      <div className="max-w-5xl mx-auto">
        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-2 flex flex-col gap-4">
            <CustomCard className="p-4 rounded-lg border border-gray-200">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-base font-semibold text-blue m-0">
                  Batch Information
                </h2>
                <CustomTag round intent={isCertExpired ? "danger" : "primary"}>
                  {isCertExpired ? "Expired Batch" : "Active Batch"}
                </CustomTag>
              </div>
              <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                <InfoRow
                  label="Sellable Item"
                  value={batch.sellableItem?.name}
                />
                <InfoRow label="Batch Code" value={batch.codename} />
                <InfoRow
                  label="Manufacturer"
                  value={batch.manufacturedBy?.name}
                />
                <InfoRow
                  label="Manufactured Date"
                  value={dayjs(batch.manufacturedOn).format("DD MMM YYYY")}
                />
                <InfoRow
                  label="Expiry Date"
                  value={dayjs(batch.expiryOn).format("DD MMM YYYY")}
                />
                <InfoRow label="Supplier" value={batch.suppliedBy?.name} />
                {batch.parentBatchStock && (
                  <InfoRow
                    label="Parent Batch"
                    value={batch.parentBatchStock.codename}
                  />
                )}
              </div>
            </CustomCard>

            {/* Attachments */}
            <CustomCard className="p-4 rounded-lg">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-base font-semibold text-blue m-0">
                  Attachments
                </h2>
                {coaUrl && (
                  <CustomTag round minimal intent="primary">
                    COA Uploaded
                  </CustomTag>
                )}
              </div>

              {/* Existing COA file */}
              {coaUrl && (
                <div className="flex items-center justify-between border border-gray-200 rounded-lg p-3 mb-3 bg-gray-50">
                  <div className="flex items-center gap-3">
                    <Icon icon="document" size={20} className="text-blue-500" />
                    <div>
                      <div className="text-sm font-medium text-gray-800">
                        COA Certificate
                      </div>
                      <div className="text-xs text-gray-400">PDF</div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <CustomButton
                      minimal
                      small
                      icon="download"
                      intent="primary"
                      onClick={() => window.open(coaUrl!, "_blank")}
                    />
                    <CustomButton
                      minimal
                      small
                      icon="cross"
                      intent="danger"
                      onClick={() => setCoaUrl(null)}
                    />
                  </div>
                </div>
              )}

              {/* Drop zone – only shown when no certificate is present */}
              {!coaUrl && (
                <div
                  className={`border-2 border-dashed rounded-lg flex flex-col items-center justify-center py-8 gap-2 cursor-pointer transition-colors ${
                    isDragging
                      ? "border-blue-400 bg-blue-50"
                      : "border-gray-200 hover:border-blue-300 hover:bg-gray-50"
                  }`}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={e => {
                    e.preventDefault()
                    setIsDragging(true)
                  }}
                  onDragLeave={() => setIsDragging(false)}
                >
                  <Icon
                    icon="document-open"
                    size={32}
                    className="text-gray-400"
                  />
                  <div className="text-sm font-medium text-gray-600">
                    Click or drag to upload
                  </div>
                  <div className="text-xs text-gray-400">
                    Support for PDF, JPG, PNG (Max 10MB)
                  </div>
                </div>
              )}

              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                className="hidden"
                disabled={!!coaUrl}
                onChange={handleUpload as any}
              />
            </CustomCard>
          </div>

          {/* ── RIGHT COLUMN ──────────────────────────────────── */}
          <div className="col-span-1">
            <CustomCard className="p-0 overflow-hidden rounded-lg">
              <div className="p-4 pb-2">
                <h2 className="text-base font-semibold text-blue m-0">
                  Stock Distribution
                </h2>
              </div>
              <div className="border-b border-slate-200">
                <div className="flex justify-between px-3 py-2 bg-slate-200">
                  <span className="text-xs font-bold uppercase tracking-wider ">
                    Stock Type
                  </span>
                  <span className="text-xs font-bold uppercase tracking-wider ">
                    Quantity
                  </span>
                </div>
              </div>

              <div className="px-3 mt-2">
                <div className="flex justify-between  border-b border-gray-200">
                  <span className="text-xs font-semibold">Current Stock</span>
                  <span className="text-xs font-bold text-green-600">
                    {batch.currentStockCount ?? 0}
                  </span>
                </div>
                <hr className="border-gray-200" />
              </div>

              <StockRow label="Hold Stock" value={batch.holdStockCount} />
              <StockRow
                label="Alternate Stock"
                value={batch.alternateStockCount}
              />
              <StockRow label="Dumped Stock" value={batch.dumpedStockCount} />
              <StockRow label="In Transit" value={batch.intransitStockCount} />

              <div className="flex justify-between items-center px-3 py-2 bg-slate-200">
                <span className="text-xs font-bold uppercase tracking-wider ">
                  Total Count
                </span>
                <span className="text-lg font-bold uppercase tracking-wider text-blue-900">
                  {totalStock}
                </span>
              </div>
            </CustomCard>
          </div>
        </div>

        <hr />
        <div className="flex justify-end">
          <CustomButton
            intent="primary"
            icon="floppy-disk"
            text="Save"
            loading={saving}
            onClick={handleSave}
          />
        </div>
      </div>
    </div>
  )
}

export default BatchDetailPage
